import { api } from "./client";

export type PolicyStatus = "UNAPPROVED" | "BUSINESS_APPROVED" | "SUPERSEDED";
export type Sections = Record<string, Record<string, string | number | boolean | null>>;

export interface PolicyVersion {
  id: string;
  version: number;
  status: PolicyStatus;
  sections: Sections;
  effective_from: string | null;
  created_at: string;
  approved_at: string | null;
  approval_note: string | null;
  advisor_validated: boolean;
  advisor_name: string | null;
  advisor_validated_at: string | null;
  advisor_note: string | null;
  template?: string;
}

export interface PolicyField {
  section: string;
  key: string;
  label: string;
  kind: "enum" | "int" | "bool" | "decimal" | "date";
  options: string[] | null;
  range: (number | string)[] | null;
  help: string;
  advisor_review: boolean;
}

export interface Banner { status: PolicyStatus; key: string; text: string; draft: boolean; advisor_required_note: string }
export interface MinWage { id: string; jurisdiction: string; cents: number; effective_from: string; note: string }

export interface PolicyResponse {
  versions: PolicyVersion[];
  latest: PolicyVersion;
  in_use: { id: string; version: number };
  banner: Banner;
  fields: PolicyField[];
  minimum_wages: MinWage[];
}

export interface JobRole { id: string; name: string; status: "ACTIVE" | "ARCHIVED" }
export interface PayRate {
  id: string;
  role_id: string;
  employee_id: string | null;
  rate_cents: number;
  effective_from: string;
  effective_to: string | null;
  reason: string;
  immutable: boolean;
  deletable: boolean;
  correctable?: boolean;
  status?: "ACTIVE" | "SUPERSEDED_BY_CORRECTION";
  is_demo?: boolean;
}

export interface PayProfile {
  id: string;
  employee_id: string;
  effective_from: string;
  ot_exempt: boolean;
  ot_exempt_reason: string | null;
  alt_schedule_4x10: boolean;
  agreement_date: string | null;
  agreement_note: string | null;
}

export interface PayPeriod { id: string; start_date: string; end_date: string; frequency: string; status: string; is_current: boolean }

export interface CalcLine {
  session_id: string;
  role_id: string | null;
  rate_cents: number;
  start: string;
  end: string;
  seconds: number;
  hours: string;
  classification: "REGULAR" | "DAILY_OT" | "WEEKLY_OT" | "WEEKLY_OT_TRUE_UP";
  multiplier: string;
  regular_rate_cents_per_hour?: string;
  amount_exact: string;
  cents: number | null;
  draft_unapproved?: boolean;
  period_id: string | null;
  note?: string;
}

export interface CalcPeriod {
  id: string;
  label: string;
  regular_seconds: number;
  ot_seconds: number;
  regular_cents: number;
  daily_ot_cents: number;
  weekly_ot_cents: number;
  weekly_ot_true_up_cents: number;
  adjustments_cents: number;
  gross_wages_cents: number;
  weekly_ot_note: string | null;
}

export interface CalcIssue { code: string; session_id?: string; message: string }
export interface Workweek { start: string; end: string; status: string; total_seconds: number; regular_rate_cents_per_hour: string }

export interface PreviewResult {
  label: string;
  banner: Banner;
  draft: boolean;
  policy_version: number;
  sessions_considered: number;
  mw_threshold_note: string;
  lines: CalcLine[];
  periods: CalcPeriod[];
  workweeks: Workweek[];
  blocking: CalcIssue[];
  warnings: CalcIssue[];
  trace: string[];
}

const P = "/admin/pay";
const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams(Object.entries(o).filter(([, v]) => v !== undefined) as [string, string][]).toString();
  return p ? `?${p}` : "";
};

export const payApi = {
  policy: () => api.get<PolicyResponse>(`${P}/policy`),
  saveDraft: (sections: Sections) => api.put<PolicyVersion & { note?: string | null }>(`${P}/policy/draft`, { sections }),
  approve: (id: string, note: string, ack: boolean) =>
    api.post<PolicyVersion>(`${P}/policy/${id}/approve`, { note, acknowledge_advisor_required: ack }),
  advisor: (id: string, advisor_name: string, validated_on: string, note: string) =>
    api.post<PolicyVersion>(`${P}/policy/${id}/advisor-validation`, { advisor_name, validated_on, note }),
  roles: () => api.get<JobRole[]>(`${P}/roles`),
  createRole: (name: string) => api.post<JobRole>(`${P}/roles`, { name }),
  updateRole: (id: string, body: { name?: string; archived?: boolean }) => api.patch<JobRole>(`${P}/roles/${id}`, body),
  rates: (role_id?: string, employee_id?: string) => api.get<PayRate[]>(`${P}/rates${qs({ role_id, employee_id })}`),
  createRate: (body: { role_id: string; employee_id: string | null; rate_cents: number; effective_from: string; reason: string }) =>
    api.post<PayRate>(`${P}/rates`, body),
  deleteRate: (id: string) => api.del<void>(`${P}/rates/${id}`),
  profiles: () => api.get<PayProfile[]>(`${P}/profiles`),
  createProfile: (body: Omit<PayProfile, "id">) => api.post<PayProfile>(`${P}/profiles`, body),
  periods: () => api.get<PayPeriod[]>(`${P}/periods`),
  generate: () => api.post<{ created: number }>(`${P}/periods/generate`),
  preview: (employee_id: string, date_from: string, date_to: string, include_unapproved = false) =>
    api.post<PreviewResult>(`${P}/preview`, { employee_id, date_from, date_to, include_unapproved }),
  correctRate: (id: string, rate_cents: number, reason: string) => api.post<PayRate>(`${P}/rates/${id}/correct`, { rate_cents, reason }),
};

export const money = (cents: number | null | undefined) =>
  cents == null ? "—" : `${cents < 0 ? "−" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;
